import { useState, useEffect, useMemo, useCallback, type ReactNode } from 'react';
import { api, unwrapApiResponse } from '@/utils/api';
import FormatoPicker, { FORMATO_ID_HENTAI } from '@/components/FormatoPicker';
import {
    normalizeFavoriteObra,
    parseContinueReadingList,
    parsePaginatedList,
} from '@/pages/colecao/collection-helpers';
import ColecaoObraCard from '@/pages/colecao/components/ColecaoObraCard';
import { Heart, BookOpen, Play, Check, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';

const ITEMS_PER_PAGE = 20;

type ReadingStatusRow = { id: number; nome: string };

interface ObraRow {
    id: number;
    nome: string;
    imagem?: string;
    formato?: { id: number; nome: string };
    total_capitulos: number;
    total_lidos?: number;
    ultimo_cap_numero?: string;
    ultimo_lido?: { cap_num?: number; numero?: number; cap_id?: number } | null;
    proximo_lido?: { cap_id?: number; cap_num?: number; numero?: number } | null;
    ultimo_publicado?: { cap_id?: number; cap_num?: number; cap_nome?: string } | null;
    [key: string]: unknown;
}

function LocalPagination({
    page,
    totalPages,
    onPageChange,
}: {
    page: number;
    totalPages: number;
    onPageChange: (p: number) => void;
}) {
    if (totalPages <= 1) return null;
    return (
        <nav className="mt-8 flex justify-center gap-2 items-center" aria-label="Paginação">
            <button
                type="button"
                disabled={page <= 1}
                onClick={() => onPageChange(page - 1)}
                className="h-10 w-10 flex items-center justify-center rounded-xl border border-white/10 bg-white/[0.02] text-white/80 hover:bg-white/10 disabled:opacity-30 disabled:pointer-events-none"
                aria-label="Página anterior"
            >
                <ChevronLeft size={18} />
            </button>
            <span className="text-sm text-white/55 tabular-nums px-2">
                {page} / {totalPages}
            </span>
            <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => onPageChange(page + 1)}
                className="h-10 w-10 flex items-center justify-center rounded-xl border border-white/10 bg-white/[0.02] text-white/80 hover:bg-white/10 disabled:opacity-30 disabled:pointer-events-none"
                aria-label="Próxima página"
            >
                <ChevronRight size={18} />
            </button>
        </nav>
    );
}

const FALLBACK_STATUSES: ReadingStatusRow[] = [
    { id: 1, nome: 'Vou ler' },
    { id: 2, nome: 'Lendo' },
    { id: 3, nome: 'Lido' },
    { id: 4, nome: 'Dropado' },
];

type SectionKey = 'favorites' | 'continue' | `status:${number}`;

export function PerfilColecaoTab({ profileUsrId }: { profileUsrId: number }) {
    const [formatoId, setFormatoId] = useState(5);
    useEffect(() => {
        if (formatoId === FORMATO_ID_HENTAI) setFormatoId(5);
    }, [formatoId]);
    const [section, setSection] = useState<SectionKey>('favorites');
    const [page, setPage] = useState(1);
    const [readingStatuses, setReadingStatuses] = useState<ReadingStatusRow[]>(FALLBACK_STATUSES);

    const [items, setItems] = useState<ObraRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await api.get('readings/statuses', { requiresAuth: true });
                const raw = unwrapApiResponse<unknown>(res);
                const arr = Array.isArray(raw) ? raw : (raw as { data?: unknown })?.data;
                if (!Array.isArray(arr) || arr.length === 0) return;
                const mapped = arr
                    .map((s: any) => ({
                        id: Number(s.stt_id ?? s.id),
                        nome: String(s.stt_nome ?? s.nome ?? '').trim(),
                    }))
                    .filter((s: ReadingStatusRow) => s.id > 0 && s.nome);
                if (!cancelled && mapped.length > 0) setReadingStatuses(mapped);
            } catch {
                /* fallback */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        setPage(1);
    }, [profileUsrId, section, formatoId]);

    const fetchSection = useCallback(async () => {
        setLoading(true);
        try {
            const base = `pagina=${page}&limite=${ITEMS_PER_PAGE}&formato=${formatoId}`;
            if (section === 'favorites') {
                const res = await api.get(
                    `favorites/perfil/${profileUsrId}?${base}&ordenar=favoritados`,
                    { requiresAuth: true },
                );
                const { items: rows, totalPages: tp, totalItems: ti } = parsePaginatedList(res);
                setItems(rows.map((row) => normalizeFavoriteObra(row) as ObraRow));
                setTotalPages(tp);
                setTotalItems(ti);
                return;
            }
            if (section === 'continue') {
                const qs = new URLSearchParams({
                    limit: String(ITEMS_PER_PAGE),
                    formato: String(formatoId),
                    pagina: String(page),
                });
                const res = await api.get(`readings/perfil/${profileUsrId}/continue?${qs}`, {
                    requiresAuth: true,
                });
                const { items: rows, totalPages: tp, totalItems: ti } =
                    parseContinueReadingList<ObraRow>(res);
                setItems(rows as ObraRow[]);
                setTotalPages(tp);
                setTotalItems(ti);
                return;
            }
            const statusId = Number(section.replace('status:', ''));
            const params = new URLSearchParams({
                limite: String(ITEMS_PER_PAGE),
                pagina: String(page),
                ordenarPor: 'criada_em_desc',
                formato: String(formatoId),
            });
            const res = await api.get(
                `readings/perfil/${profileUsrId}/by-status/${statusId}?${params}`,
                { requiresAuth: true },
            );
            const { items: rows, totalPages: tp, totalItems: ti } = parsePaginatedList<ObraRow>(res);
            setItems(rows);
            setTotalPages(tp);
            setTotalItems(ti);
        } catch {
            setItems([]);
            setTotalPages(1);
            setTotalItems(0);
        } finally {
            setLoading(false);
        }
    }, [profileUsrId, section, page, formatoId]);

    useEffect(() => {
        void fetchSection();
    }, [fetchSection]);

    const statusIcons: Record<string, ReactNode> = {
        lendo: <Play size={16} fill="currentColor" />,
        'vou ler': <BookOpen size={16} />,
        lido: <Check size={16} />,
        dropado: <Trash2 size={16} />,
    };

    const sectionLabel = useMemo(() => {
        if (section === 'favorites') return 'Favoritos';
        if (section === 'continue') return 'Continue lendo';
        const id = Number(section.replace('status:', ''));
        return readingStatuses.find((s) => s.id === id)?.nome ?? 'Lista';
    }, [section, readingStatuses]);

    const cardContext = useMemo(() => {
        if (section === 'favorites') return 'favorite' as const;
        if (section === 'continue') return 'continue' as const;
        return 'status' as const;
    }, [section]);

    const navButton = (key: SectionKey, label: string, icon: ReactNode) => (
        <button
            key={key}
            type="button"
            onClick={() => setSection(key)}
            className={`flex w-full items-center gap-2 rounded-xl px-4 py-2.5 text-left text-sm font-semibold transition-all ${
                section === key
                    ? 'bg-orange-600 text-white'
                    : 'text-gray-400 hover:bg-neutral-800/60 hover:text-white'
            }`}
        >
            {icon}
            {label}
        </button>
    );

    return (
        <div className="text-white">

            <div className="grid items-start gap-6 md:grid-cols-[220px_minmax(0,1fr)]">
                <aside className="sticky top-24 hidden rounded-2xl border border-neutral-800 bg-neutral-900/50 p-2 backdrop-blur-xl md:block">
                    <div className="space-y-1">
                        {navButton('favorites', 'Favoritos', <Heart size={16} className={section === 'favorites' ? 'fill-white' : ''} />)}
                        {navButton('continue', 'Continue lendo', <BookOpen size={16} />)}
                        <div className="my-2 h-px bg-neutral-800" />
                        {readingStatuses.map((st) => {
                            const key = `status:${st.id}` as SectionKey;
                            const sk = st.nome.toLowerCase();
                            return (
                                <button
                                    key={st.id}
                                    type="button"
                                    onClick={() => setSection(key)}
                                    className={`flex w-full items-center gap-2 rounded-xl px-4 py-2.5 text-left text-sm font-semibold transition-all ${
                                        section === key
                                            ? 'bg-orange-600 text-white'
                                            : 'text-gray-400 hover:bg-neutral-800/60 hover:text-white'
                                    }`}
                                >
                                    <span className="flex items-center gap-2">
                                        {statusIcons[sk] || <BookOpen size={16} />}
                                        {st.nome}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </aside>

                <div className="min-w-0 md:hidden">
                    <div className="flex overflow-x-auto no-scrollbar gap-1 p-1.5 bg-neutral-900/50 backdrop-blur-xl border border-neutral-800 rounded-2xl mb-6">
                        <button
                            type="button"
                            onClick={() => setSection('favorites')}
                            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm whitespace-nowrap ${
                                section === 'favorites' ? 'bg-orange-600 text-white' : 'text-gray-400'
                            }`}
                        >
                            <Heart size={16} className={section === 'favorites' ? 'fill-white' : ''} /> Favoritos
                        </button>
                        <button
                            type="button"
                            onClick={() => setSection('continue')}
                            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm whitespace-nowrap ${
                                section === 'continue' ? 'bg-orange-600 text-white' : 'text-gray-400'
                            }`}
                        >
                            <BookOpen size={16} /> Continue
                        </button>
                        <div className="w-px h-6 bg-neutral-800 self-center mx-1" />
                        {readingStatuses.map((st) => {
                            const key = `status:${st.id}` as SectionKey;
                            return (
                                <button
                                    key={st.id}
                                    type="button"
                                    onClick={() => setSection(key)}
                                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm whitespace-nowrap ${
                                        section === key ? 'bg-orange-600 text-white' : 'text-gray-400'
                                    }`}
                                >
                                    {st.nome}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="min-w-0 pb-8">
                    <div className="mb-6">
                        <FormatoPicker
                            onSelect={setFormatoId}
                            selectedFormato={formatoId}
                            excludeFormatIds={[FORMATO_ID_HENTAI]}
                        />
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-6 px-0.5">
                        <p className="text-sm text-white/55">
                            <span className="text-white font-semibold">{sectionLabel}</span>
                            {totalItems > 0 && (
                                <>
                                    {' '}
                                    · <span className="text-white font-bold tabular-nums">{totalItems}</span>{' '}
                                    {totalItems === 1 ? 'obra' : 'obras'}
                                </>
                            )}
                        </p>
                    </div>

                    {loading && items.length === 0 ? (
                        <div className="flex flex-col items-center py-16">
                            <div className="w-10 h-10 border-3 border-orange-500/30 border-t-orange-500 rounded-full animate-spin mb-4" />
                            <p className="text-gray-400 text-sm">A carregar…</p>
                        </div>
                    ) : items.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20 rounded-2xl border border-white/10 bg-white/[0.03]">
                            <BookOpen className="w-16 h-16 text-white/15 mb-4" />
                            <p className="text-white/45 text-center px-4">
                                Nada nesta secção para o formato escolhido.
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 mb-8">
                                {items.map((work) => (
                                    <div
                                        key={work.id}
                                        className="rounded-xl border border-white/10 bg-white/[0.02] p-2 hover:border-orange-500/25 transition-colors group"
                                    >
                                        <ColecaoObraCard
                                            context={cardContext}
                                            work={work}
                                            hideFootActionButtons
                                            overlayEnd={
                                                work.formato?.nome ? (
                                                    <span className="px-2 py-0.5 bg-black/55 backdrop-blur-md rounded text-[10px] font-semibold uppercase tracking-wider text-white/90 border border-white/10">
                                                        {work.formato.nome}
                                                    </span>
                                                ) : null
                                            }
                                        />
                                    </div>
                                ))}
                            </div>
                            <LocalPagination page={page} totalPages={totalPages} onPageChange={setPage} />
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
